import React, { useEffect } from 'react';
import BigNumber from 'bignumber.js';
import { BoxProps } from '@mui/material';
import { TransactionResponse } from '@ethersproject/providers';
import { Trans } from '@lingui/macro';

import { queryClient } from '../../../../pages/_app.page';
import { QueryKeys } from '../../../ui-config/queries';
import { useRootStore } from '../../../store/root';
import { getErrorTextFromError, TxAction } from '../../../ui-config/errorMapping';
import { useModalContext } from '../../../hooks/useModal';
import { useWeb3Context } from '../../../libs/hooks/useWeb3Context';
import { useRequestWithFlashloanProvider } from '../../../hooks/yldr/useRequestWithFlashloanProvider';
import { ComputedReserveData } from '../../../hooks/app-data-provider/useAppDataProvider';
import { TxPositionActionsWrapper } from '../TxPositionActionsWrapper';

export interface LeveragePositionActionsProps extends BoxProps {
  marketId?: number;
  tokenId?: string;
  isWrongNetwork: boolean;
  tokenToBorrow?: ComputedReserveData;
  amountToBorrow: BigNumber;
  slippage: number;
}

export const LeveragePositionActions = React.memo(
  ({ marketId, tokenId, isWrongNetwork, tokenToBorrow, amountToBorrow, slippage }: LeveragePositionActionsProps) => {
    const requestWithFlashloanProvider = useRequestWithFlashloanProvider();
    const { mainTxState, setMainTxState, setTxError, setGasLimit } = useModalContext();
    const { sendTx } = useWeb3Context();

    const [leveragePosition] = useRootStore((state) => [state.leveragePosition]);

    const action = async () => {
      if (!marketId || !tokenId || !tokenToBorrow || amountToBorrow.isLessThanOrEqualTo(0) || mainTxState.success) return;

      try {
        window.gtag('event', 'leverage_modal_leverage_position', {
          asset: tokenToBorrow.symbol,
          amountToBorrow: amountToBorrow,
        });

        setTxError(undefined);
        setMainTxState({ ...mainTxState, loading: true });

        const leverageTxData = await requestWithFlashloanProvider({
          fn: leveragePosition,
          args: [marketId, tokenId, tokenToBorrow.underlyingAsset, amountToBorrow.toFixed(), slippage],
        });

        const response: TransactionResponse = await sendTx(leverageTxData);
        await response.wait(1);

        setMainTxState({
          txHash: response.hash,
          loading: false,
          success: true,
        });

        queryClient.invalidateQueries({ queryKey: [QueryKeys.POSITIONS] });
        queryClient.invalidateQueries({ queryKey: [QueryKeys.POSITIONS_DATA] });
        queryClient.invalidateQueries({ queryKey: [QueryKeys.LEVERAGED_POSITIONS] });
        queryClient.invalidateQueries({ queryKey: [QueryKeys.LEVERAGED_POSITIONS_DATA] });

      } catch (error) {
        const parsedError = getErrorTextFromError(error, TxAction.GAS_ESTIMATION, false);
        setTxError(parsedError);
        setMainTxState({
          txHash: undefined,
          loading: false,
        });
      }
    };

    // Update gas estimation
    useEffect(() => {
      if (!marketId || !tokenId || !tokenToBorrow || amountToBorrow.isLessThanOrEqualTo(0) || mainTxState.success) return;

      const calculateGasLimit = async () => {
        const leverageTxData = await requestWithFlashloanProvider({
          fn: leveragePosition,
          args: [marketId, tokenId, tokenToBorrow.underlyingAsset, amountToBorrow.toFixed(), slippage],
        });
        setGasLimit(leverageTxData.gasLimit?.toString() || '0');
      }

      const timerId = setTimeout(calculateGasLimit, 500);
      const intervalId = setInterval(calculateGasLimit, 15000);
      return () => {
        clearInterval(intervalId);
        clearTimeout(timerId);
      }
    }, [tokenToBorrow?.underlyingAsset, amountToBorrow.toFixed(), mainTxState.success]);

    return (
      <TxPositionActionsWrapper
        mainTxState={mainTxState}
        isWrongNetwork={isWrongNetwork}
        requiresAmount
        preparingTransactions={false}
        actionText={<Trans>Leverage Position</Trans>}
        actionInProgressText={<Trans>Leveraging Position</Trans>}
        handleAction={action}
        chooseLeverage={amountToBorrow.isLessThanOrEqualTo(0)}
      />
    );
  }
);
